import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import activePointIcon from '../assets/icons/active-point.svg';
import passivePointIcon from '../assets/icons/passive-point.svg';
import openOrderIcon from '../assets/icons/right-icon.svg';
import spentPointIcon from '../assets/icons/spent-point.svg';
import totalPointIcon from '../assets/icons/total-point.svg';
import { PointDetailDialog } from '../container/PointDetailDialog';
import { lightTheme } from '../muiTheme';
import '../styles/Points.css';
import { formattedDate } from '../utils';
import {
  ICalculatedIndividualPoints,
  IIndividualPointResult,
  PointType,
} from '../utils/types';
import { PointStateCard } from './PointStateCard';
import SvgIcon from './SvgIcon';

type PointsProps = {
  points: IIndividualPointResult[];
  calculatedPoints?: ICalculatedIndividualPoints;
};

export default function Points({ points, calculatedPoints }: PointsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [selectedPoint, setSelectedPoint] =
    useState<IIndividualPointResult | null>(null);

  const [filteredPoints, setFilteredPoints] =
    useState<IIndividualPointResult[]>(points);

  const filterPoints = (
    points: IIndividualPointResult[],
    filterCondition: (point: IIndividualPointResult) => boolean,
  ): IIndividualPointResult[] => {
    return points.filter(filterCondition);
  };

  const handlePointCardClick = (
    filterCondition: (point: IIndividualPointResult) => boolean,
  ) => {
    const filtered = filterPoints(points, filterCondition);
    setFilteredPoints(filtered);
  };

  return (
    <Box>
      {filteredPoints.length > 0 && (
        <>
          <Paper sx={{ mb: 2, pt: 2 }}>
            <Grid2 container spacing={2} sx={{ mb: 2 }} justifyContent="center">
              <Grid2 xs={6} lg={3}>
                <PointStateCard
                  title={t('points.totalPoint')}
                  value={calculatedPoints?.totalPoint}
                  imageUrl={totalPointIcon}
                  color="success"
                  onClick={() => handlePointCardClick(() => true)}
                />
              </Grid2>
              <Grid2 xs={6} lg={3}>
                <PointStateCard
                  title={t('points.spentPoint')}
                  value={calculatedPoints?.debitedPoint}
                  imageUrl={spentPointIcon}
                  color="#D31607"
                  onClick={() =>
                    handlePointCardClick((point) => point.pointType === 'DEBIT')
                  }
                />
              </Grid2>
              <Grid2 xs={6} lg={3}>
                <PointStateCard
                  title={t('points.activePoint')}
                  value={calculatedPoints?.activePoint}
                  imageUrl={activePointIcon}
                  color="#1A6F38"
                  onClick={() =>
                    handlePointCardClick(
                      (point) =>
                        point.pointType === 'CREDIT' &&
                        point.status === 'ACTIVE',
                    )
                  }
                />
              </Grid2>
              <Grid2 xs={6} lg={3}>
                <PointStateCard
                  title={t('points.blockedPoint')}
                  value={calculatedPoints?.blockedPoint}
                  imageUrl={passivePointIcon}
                  color="#7B1007"
                  onClick={() =>
                    handlePointCardClick(
                      (point) =>
                        point.pointType === 'CREDIT' &&
                        point.status === 'BLOCKED',
                    )
                  }
                />
              </Grid2>
            </Grid2>
          </Paper>

          {!isMobile ? (
            <PointsTableDesktop points={filteredPoints} />
          ) : (
            <PointsTableMobile
              points={filteredPoints}
              onSelectPoint={(p) => setSelectedPoint(p)}
            />
          )}
        </>
      )}

      {!points.length && <Box>{t('points.noRecord')}</Box>}

      {selectedPoint && (
        <PointDetailDialog
          open={true}
          point={selectedPoint}
          onClose={() => setSelectedPoint(null)}
        />
      )}
    </Box>
  );
}

type PointsMobileTable = {
  points: IIndividualPointResult[];
  onSelectPoint: (point: IIndividualPointResult) => void;
};

function PointsTableMobile({ points, onSelectPoint }: PointsMobileTable) {
  const { t } = useTranslation();
  const getPointTypeLabel = (pointType?: PointType) => {
    switch (pointType) {
      case 'CREDIT':
        return t('pointsTableMobile.credit');
      case 'DEBIT':
        return t('pointsTableMobile.debit');
      case 'CREDITREFUND':
        return t('pointsTableMobile.creditRefund');
      case 'DEBITREFUND':
        return t('pointsTableMobile.debitRefund');
      case 'TRANSFER':
        return t('pointsTableMobile.transfer');
      case 'TRANSFERREFUND':
        return t('pointsTableMobile.transferRefund');
    }
  };

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell component="th">
              {t('pointsTableMobile.transaction')}
            </TableCell>
            <TableCell component="th">{t('pointsTableMobile.name')}</TableCell>
            <TableCell component="th">{t('pointsTableMobile.date')}</TableCell>
            <TableCell component="th">{t('pointsTableMobile.point')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {points.map((p, index) => (
            <TableRow key={index}>
              <TableCell>{getPointTypeLabel(p.pointType)}</TableCell>
              <TableCell>{p.customer}</TableCell>
              <TableCell>{formattedDate(p.transactionDate)}</TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ color: 'secondary', fontSize: '12px' }}>
                    {p.point || '-'}
                  </Box>

                  <IconButton
                    aria-label="Toggle open order"
                    onClick={() => onSelectPoint(p)}
                    sx={{
                      cursor: 'pointer',
                      width: 30,
                      height: 30,
                      ml: 3,
                      borderRadius: '50%',
                      '&:hover': {
                        background: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    <SvgIcon
                      src={openOrderIcon}
                      style={{
                        fill: lightTheme.palette?.secondary?.toString(),
                        width: 30,
                        height: 30,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

type PointsTableDesktopProps = {
  points: IIndividualPointResult[];
};
function PointsTableDesktop({ points }: PointsTableDesktopProps) {
  const { t } = useTranslation();

  const getPointTypeLabel = (pointType?: PointType) => {
    switch (pointType) {
      case 'CREDIT':
        return t('pointsTableMobile.credit');
      case 'DEBIT':
        return t('pointsTableMobile.debit');
      case 'CREDITREFUND':
        return t('pointsTableMobile.creditRefund');
      case 'DEBITREFUND':
        return t('pointsTableMobile.debitRefund');
      case 'TRANSFER':
        return t('pointsTableMobile.transfer');
      case 'TRANSFERREFUND':
        return t('pointsTableMobile.transferRefund');
    }
  };

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell component="th">
              {t('pointsTableDesktop.transaction')}
            </TableCell>
            <TableCell component="th">
              {t('pointsTableDesktop.point')}
            </TableCell>
            <TableCell component="th">
              {t('pointsTableDesktop.orderNumber')}
            </TableCell>
            <TableCell component="th">
              {t('pointsTableDesktop.customer')}
            </TableCell>
            <TableCell style={{ width: '150px' }}>
              {t('pointsTableDesktop.material')}
            </TableCell>
            <TableCell component="th">
              {t('pointsTableDesktop.validity')}
            </TableCell>
            <TableCell component="th">
              {t('pointsTableDesktop.description')}
            </TableCell>
          </TableRow>
        </TableHead>
        {points.map((p, index) => (
          <TableBody key={index}>
            <TableRow>
              <TableCell>
                {formattedDate(p.transactionDate)}
                <br />
                <Typography color="text.secondary" fontSize="small">
                  {getPointTypeLabel(p.pointType)}
                </Typography>
              </TableCell>
              <TableCell>
                {p.point || '-'}
                <br />
                <Typography color="text.secondary" fontSize="small">
                  {p.status === 'ACTIVE' && t(`pointStatus.active`)}
                  {p.status === 'BLOCKED' && t(`pointStatus.blocked`)}
                </Typography>
              </TableCell>
              <TableCell>{p.orderNumber || '-'}</TableCell>
              <TableCell>{p.customer || '-'}</TableCell>
              <TableCell>{p.material || '-'}</TableCell>
              <TableCell>
                {formattedDate(p.startDate)}
                <br />
                {formattedDate(p.endDate)}
              </TableCell>
              <TableCell>{p.description || '-'}</TableCell>
            </TableRow>
          </TableBody>
        ))}
      </Table>
    </Paper>
  );
}
